import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Save,
  Trash2,
  History,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminDeleteLabourTemplate,
  adminGetLabourTemplate,
  adminSaveLabourTemplate,
} from "@/lib/api/labour-analyzer.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/portal/admin/labour-templates/$id")({
  head: () => ({
    meta: [
      { title: "Admin · Edit labour-law template" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: EditTemplate,
});

const CATEGORIES = [
  "system_prompt",
  "ccma",
  "lra",
  "bcea",
  "eea",
  "schedule_8",
  "case_law",
  "drafting",
  "general",
];

function EditTemplate() {
  const { id } = Route.useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const fetchOne = useServerFn(adminGetLabourTemplate);
  const save = useServerFn(adminSaveLabourTemplate);
  const del = useServerFn(adminDeleteLabourTemplate);

  const q = useQuery({
    queryKey: ["admin-labour-template", id],
    queryFn: () => fetchOne({ data: { id } }),
  });

  const row = q.data?.row;
  const history = q.data?.history ?? [];

  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("general");
  const [body, setBody] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishNew, setPublishNew] = useState(false);
  const [setActiveOnSave, setSetActive] = useState(false);

  useEffect(() => {
    if (row) {
      setSlug(row.slug);
      setTitle(row.title);
      setCategory(row.category);
      setBody(row.body_md);
      setNotes(row.notes ?? "");
      setSetActive(row.is_active);
    }
  }, [row?.id]);

  async function onSave() {
    setSaving(true);
    try {
      const res = await save({
        data: {
          id: publishNew ? null : id,
          slug,
          title,
          category: category as any,
          body_md: body,
          notes,
          publishAsNewVersion: publishNew,
          setActive: setActiveOnSave,
        },
      });
      toast.success(
        res.action === "created" ? `Published as v${res.version}` : "Saved",
      );
      qc.invalidateQueries({ queryKey: ["admin-labour-templates"] });
      qc.invalidateQueries({ queryKey: ["admin-labour-template", id] });
      if (res.action === "created") {
        nav({
          to: "/portal/admin/labour-templates/$id" as any,
          params: { id: res.id } as any,
        });
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (!confirm("Delete this version permanently?")) return;
    try {
      await del({ data: { id } });
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["admin-labour-templates"] });
      nav({ to: "/portal/admin/labour-templates" as any });
    } catch (e: any) {
      toast.error(e?.message ?? "Delete failed");
    }
  }

  if (q.isLoading || !row) {
    return (
      <p className="text-sm text-muted-foreground">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to={"/portal/admin/labour-templates" as any}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-navy"
      >
        <ArrowLeft className="h-4 w-4" /> All templates
      </Link>

      <header className="rounded-2xl border border-border bg-gradient-to-br from-navy/[0.04] to-gold/[0.06] p-6 shadow-soft">
        <h1 className="font-display text-2xl text-navy">{row.title}</h1>
        <p className="mt-1 text-xs text-muted-foreground">
          slug <span className="font-mono">{row.slug}</span> · v{row.version} ·{" "}
          {row.is_active ? (
            <span className="font-semibold text-emerald-700">ACTIVE</span>
          ) : (
            "inactive"
          )}
        </p>
      </header>

      <section className="space-y-3 rounded-xl border border-border bg-card p-5 shadow-soft">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label>Slug (stable id)</Label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
              maxLength={80}
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              All versions sharing a slug are siblings — keep it stable.
            </p>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c.replaceAll("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-muted-foreground">
              <strong>system_prompt</strong> rows extend the analyser prompt.
              <strong> drafting</strong> rows extend draft generation.{" "}
              <strong>ccma</strong> rows are added when drafting CCMA documents.
            </p>
          </div>
        </div>

        <div>
          <Label>Title</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
          />
        </div>

        <div>
          <Label>Body (Markdown — fed directly to the AI)</Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={20}
            className="font-mono text-xs"
            maxLength={60000}
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            {body.length.toLocaleString()} characters
          </p>
        </div>

        <div>
          <Label>Version notes (changelog)</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
        </div>

        <div className="grid gap-2 rounded-md border border-border bg-muted/30 p-3 sm:grid-cols-2">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={publishNew}
              onChange={(e) => setPublishNew(e.target.checked)}
            />
            Publish as new version (keeps history)
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={setActiveOnSave}
              onChange={(e) => setSetActive(e.target.checked)}
            />
            Make this the active version
          </label>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={onSave} disabled={saving} className="bg-navy hover:bg-navy/90">
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {publishNew ? "Publish new version" : "Save changes"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onDelete}
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
          >
            <Trash2 className="mr-2 h-4 w-4" /> Delete this version
          </Button>
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card p-5 shadow-soft">
        <h3 className="mb-3 text-sm font-semibold text-navy">
          <History className="mr-1 inline h-4 w-4" /> Version history
        </h3>
        <ul className="space-y-1 text-xs">
          {history.map((h: any) => (
            <li
              key={h.id}
              className="flex items-center justify-between rounded-md border border-border bg-muted/20 px-3 py-2"
            >
              <div className="min-w-0">
                <span className="font-mono">v{h.version}</span> · {h.title}
                {h.notes && (
                  <span className="ml-2 text-muted-foreground">— {h.notes}</span>
                )}{" "}
                <span className="ml-2 text-[10px] text-muted-foreground">
                  {new Date(h.updated_at).toLocaleString("en-ZA")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {h.is_active && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-900">
                    <CheckCircle2 className="h-3 w-3" /> active
                  </span>
                )}
                {h.id !== id && (
                  <Link
                    to={"/portal/admin/labour-templates/$id" as any}
                    params={{ id: h.id } as any}
                    className="text-muted-foreground hover:text-navy"
                  >
                    Open
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Tip: keep the seed v1 around as a known-good baseline; publish edits as new
        versions so you can always roll back.
      </p>
    </div>
  );
}
